import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';

import { TXNS, CREATE_TXN } from '../Txns/queries';
import METHODS from '../Txns/methods';
import {
  visibleAccounts,
  specialAccount,
  prepareTxnInput,
} from '../Txns/utils';

export const FIELDS = {
  bankDate: {
    label: 'Date',
    type: 'date',
    rules: { required: true },
  },
  balanceAccountId: {
    label: 'Payment Account',
    type: 'account',
    rules: { required: true },
    preferredTypes: [
      'Bank', 'Accounts Payable', 'Credit Card', 'Other Current Asset'
    ],
    preferredOnly: true,
  },
  method: {
    label: 'Payment Method',
    select: METHODS,
    blank: 'null',
  },
  addToAchBatch: {
    label: 'Add to current ACH Batch?',
    type: 'boolean',
    disabled: true,
  },
  reference: {
    label: 'Reference #',
  },
  name: {
    type: 'entityName',
    preferredTypes: ['Buyer'],
  },
  memo: {},
};

export const LINE_FIELDS = {
  accountId: {
    label: 'Account',
    type: 'account',
    preferredTypes: ['Income'],
  },
  debitLessCredit: {
    label: 'Amount',
  },
  assetId: {
    label: 'Asset',
    type: 'asset',
    createFee: true,
    width: 140,
    extraLabels: {
      saleAssetId: 'Lot/Sale',
    },
  },
  memo: { },
};

export const EMPTY_LINE = {
  accountId: '',
  debitLessCredit: '',
  memo: '',
};

const NewCustomerRefund = ({ setShowModal, refetch, asset, saleAsset }) => {
  const { accounts, paymentTerms } = useContext(AuthContext);

  const salesAccount = accounts ?
    specialAccount({ accounts, special: 'sales' }) : null;

  const emptyLines = [{
    ...EMPTY_LINE,
    accountId: salesAccount?.id,
    assetId: asset?.id,
    saleAssetId: saleAsset?.id,
  }];

  console.log(emptyLines, saleAsset);

  const [nestedResources, setNestedResources] = useState(emptyLines);
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const resource = { bankDate: DateTime.now().toISODate() };

  if (saleAsset) {
    const { buyer } = saleAsset.soldOffer;
    resource.name = buyer.displayName;
    resource.entityType = buyer.__typename;
    resource.entityId = buyer.id;
  }

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource,
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [createTxn] = useMutation(CREATE_TXN, {
    onCompleted: data => {
      if (data.createTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.createTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = 'Customer Refund';
    createTxn({ variables: prepareTxnInput(input, fields) });
  });

  const watchAll = watch();

  const addLine = () => {
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: salesAccount?.id });
    register(`${prefix}.debitLessCredit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  };

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.debitLessCredit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: salesAccount?.id });
        register(`${prefix}.debitLessCredit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  const fields = { ...FIELDS };
  const lineFields = { ...LINE_FIELDS };

  if (watchAll) {
    if (watchAll.addToAchBatch || 'ACH' === watchAll.method) {
      if (fields.addToAchBatch.disabled) {
        fields.addToAchBatch = { ...fields.addToAchBatch, disabled: false };
      }
    } else {
      if (!fields.addToAchBatch.disabled) {
        fields.addToAchBatch = { ...fields.addToAchBatch, disabled: true };
      }
    }
  }

  return (
    <View>
      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <Pressable onPress={addLine}>
          <Text style={{ fontSize: 14 }}>+ Add Line</Text>
        </Pressable>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button
            label="Save Customer Refund"
            onPress={handleSubmit(onSubmit)}
            wide
          />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default NewCustomerRefund;
